import React, { useEffect, useState, useRef, useCallback, useMemo } from "react";
import { AgGridReact } from "ag-grid-react";
import {
    ColDef,
    GridReadyEvent,
    GetRowIdFunc,
    RowClassParams,
    RowStyle,
    RowSelectionOptions 
} from "ag-grid-community";
import { useMediaQuery, useTheme } from "@mui/material";

/* CSS AG Grid */
import "ag-grid-community/styles/ag-grid.css";
import "ag-grid-community/styles/ag-theme-quartz.css";

/* ===================== INTERFACCE ===================== */

export type CustomGridColDef = ColDef & {
    context?: {
        /** 1: smartphone, 2: tablet, 3: desktop. */
        importance?: number;
        /** Mostra la colonna soltanto oltre i 1600 px di viewport. */
        wideDesktopOnly?: boolean;
        /** Larghezza compatta applicata alla colonna sugli smartphone. */
        mobileWidth?: number;
    };
};

export interface DataGridProps {
    rows: any[];
    columns: CustomGridColDef[];
    checkboxSelection?: boolean;
    onRowClick?: (row: any) => void;
    themeVariant?: "light" | "dark";
    sx?: any;
    style?: React.CSSProperties;
    disablePagination?: boolean;
    rowHeight?: number;
    getRowStyle?: (params: RowClassParams) => RowStyle | undefined;
    onGridReady?: (event: GridReadyEvent) => void;
    getRowId?: GetRowIdFunc;
    preserveScrollPosition?: boolean;
}

/* ===================== CONFIGURAZIONE TEMI ===================== */

export const agDarkThemeParams = {
    '--ag-background-color': '#3d464dff',
    '--ag-foreground-color': '#F8F9FA',
    '--ag-card-background-color': '#343A40',
    '--ag-border-color': 'rgba(255, 255, 255, 0.08)',
    '--ag-row-border-color': 'rgba(255, 255, 255, 0.08)',
    '--ag-header-background-color': '#343A40',
    '--ag-header-foreground-color': '#80CC00',
    '--ag-accent-color': 'rgba(46, 125, 50, 0.7)',
    '--ag-selected-row-background-color': 'rgba(128,204,0,0.10)',
    '--ag-row-hover-color': 'rgba(255,255,255,0.05)',
    '--ag-secondary-foreground-color': '#ADB5BD',
    '--ag-input-background-color': '#212529',
    '--ag-input-border-color': 'rgba(255,255,255,0.15)',
    '--ag-input-focus-border-color': 'rgba(46, 125, 50, 0.7)',
    '--ag-font-family': 'Roboto, Arial, sans-serif',
    '--ag-font-weight': '300',
    '--ag-link-color': 'rgba(46, 125, 50, 0.7)',
    '--ag-link-hover-color': 'rgba(46, 125, 50, 0.7)',
    '--ag-font-size': '12px'
};

export const agLightThemeParams = {
    '--ag-background-color': '#ebe8e8ff',
    '--ag-foreground-color': '#212529',
    '--ag-card-background-color': '#F8F9FA',
    '--ag-border-color': 'rgba(0,0,0,0.08)',
    '--ag-row-border-color': 'rgba(0,0,0,0.08)',
    '--ag-header-background-color': '#E9ECEF',
    '--ag-header-foreground-color': '#80CC00',
    '--ag-accent-color': '#80CC00',
    '--ag-selected-row-background-color': 'rgba(128,204,0,0.10)',
    '--ag-row-hover-color': 'rgba(0,0,0,0.03)',
    '--ag-secondary-foreground-color': '#495057',
    '--ag-input-background-color': '#FFFFFF',
    '--ag-input-border-color': 'rgba(0,0,0,0.15)',
    '--ag-input-focus-border-color': '#80CC00',
    '--ag-font-family': 'Roboto, Arial, sans-serif',
    '--ag-font-weight': '300',
    '--ag-link-color': '#339AF0',
    '--ag-link-hover-color': '#1C7ED6',
    '--ag-font-size': '12px',
};



/* ===================== COMPONENTE CORE ===================== */

export const DataGridCustom: React.FC<DataGridProps> = ({
    rows,
    columns,
    checkboxSelection = false,
    onRowClick,
    themeVariant = "dark",
    style,
    disablePagination = true,
    rowHeight = 30,
    getRowStyle,
    onGridReady: onReady,
    getRowId,
    preserveScrollPosition = false,
}) => {
    const muiTheme = useTheme();
    const isPhone = useMediaQuery(muiTheme.breakpoints.down("sm"));
    const isTablet = useMediaQuery(muiTheme.breakpoints.between("sm", "lg"));
    const isWideDesktop = useMediaQuery("(min-width: 1601px)");
    const gridRef = useRef<AgGridReact>(null);
    const containerRef = useRef<HTMLDivElement>(null);
    const [gridApi, setGridApi] = useState<any>(null);

    const mappedColumns = useMemo(() => {
        const visibleImportance = isPhone ? 1 : isTablet ? 2 : 3;
        return columns.map((col) => {
            const mobileWidth = isPhone ? col.context?.mobileWidth : undefined;
            return {
                ...col,
                ...(mobileWidth ? { width: mobileWidth, minWidth: mobileWidth } : {}),
                hide: col.hide === true
                    || (col.context?.importance ?? 3) > visibleImportance
                    || (col.context?.wideDesktopOnly === true && !isWideDesktop),
                sortable: col.sortable ?? true,
                filter: col.filter ?? true,
                resizable: col.resizable ?? true,
            };
        });
    }, [columns, isPhone, isTablet, isWideDesktop]);

    // 🚩 CORREZIONE TYPE: singleRow / multiRow
    const rowSelection = useMemo((): RowSelectionOptions => {
        return {
            mode: checkboxSelection ? 'multiRow' : 'singleRow',
            checkboxes: checkboxSelection,
            headerCheckbox: checkboxSelection,
            enableClickSelection: !checkboxSelection,
        };
    }, [checkboxSelection]);

    const themeStyle = useMemo(() =>
        themeVariant === 'dark' ? agDarkThemeParams : agLightThemeParams
        , [themeVariant]);

    const themeClassName = useMemo(() =>
        themeVariant === 'dark' ? 'ag-theme-quartz-dark' : 'ag-theme-quartz'
        , [themeVariant]);

    const onGridReady = useCallback((params: GridReadyEvent) => {
        setGridApi(params.api);
        params.api.sizeColumnsToFit();
        onReady?.(params);
    }, [onReady]);

    useEffect(() => {
        if (gridApi && !preserveScrollPosition) {
            gridApi.resetRowHeights();
        }
    }, [rows, gridApi, preserveScrollPosition]);

    useEffect(() => {
        if (!gridApi || gridApi.isDestroyed()) return;
        requestAnimationFrame(() => {
            if (!gridApi.isDestroyed()) gridApi.sizeColumnsToFit();
        });
    }, [gridApi, mappedColumns]);

    useEffect(() => {
        if (!gridApi || !containerRef.current) return;
        const resize = () => {
            if (!gridApi.isDestroyed()) gridApi.sizeColumnsToFit();
        };
        const observer = new ResizeObserver(resize);
        observer.observe(containerRef.current);
        resize();
        return () => observer.disconnect();
    }, [gridApi]);

    const handleRowClicked = useCallback((e: any) => {
        if (onRowClick) {
            const target = e.event?.target as HTMLElement;
            const isInteractive = target.closest('a') || target.closest('button');
            if (isInteractive) return;
            onRowClick(e.data);
        }
    }, [onRowClick]);

    return (
        <div
            ref={containerRef}
            key={themeVariant}
            className={themeClassName}
            style={{
                height: "100%",
                width: "100%",
                ...themeStyle,
                ...style
            }}
        >
            <AgGridReact
                ref={gridRef}
                theme="legacy"
                rowData={rows}
                getRowId={getRowId}
                suppressScrollOnNewData={preserveScrollPosition}
                columnDefs={mappedColumns}
                rowSelection={rowSelection} 
                animateRows={true}
                suppressColumnVirtualisation={false}
                onRowClicked={handleRowClicked}
                onGridReady={onGridReady}
                pagination={!disablePagination}
                rowHeight={rowHeight}
                getRowStyle={getRowStyle}
            />
        </div>
    );
};
